import { Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from './AppLayout.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { DesignPage } from './pages/DesignPage.js';
import { CandidatesPage } from './pages/CandidatesPage.js';
import { AddCandidatePage } from './pages/AddCandidatePage.js';
import { PersonPage } from './pages/PersonPage.js';
import { OfferReviewPage } from './pages/OfferReviewPage.js';
import { OfferSendPage } from './pages/OfferSendPage.js';
import { OffersPage } from './pages/OffersPage.js';
import { RosterPage } from './pages/RosterPage.js';
import { ReviewQueuePage } from './pages/ReviewQueuePage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { SignedOutPage } from './pages/SignedOutPage.js';
import { PortalPage } from './portal/PortalPage.js';
/* plop:imports */
import { PortalLinksPage } from './pages/PortalLinksPage.js';
import { InterviewsPage } from './pages/InterviewsPage.js';
import { EmployeeProfilePage } from './pages/EmployeeProfilePage.js';
import { FormsPage } from './pages/FormsPage.js';

/**
 * Two route groups (PLAN §4):
 *   /app    — HR admin surface; Entra SSO is enforced in #9.
 *   /portal — the "visitor" surface for candidates and field staff (ADR-004):
 *             no account, no AppShell chrome, reached only by a scoped token link.
 */
export const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/app" replace /> },
  {
    path: '/app',
    element: <AppLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'people', element: <CandidatesPage /> },
      /*
        React Router ranks by specificity, not by array order, so the static segment wins here
        whichever way round these two sit. What it does NOT do is stop `useMatch('/app/people/:id')`
        elsewhere from matching this path with `id: 'new'` — the shell's breadcrumb did exactly that
        and fired `GET /api/people/new`. Anything matching the person route must exclude this one.
      */
      { path: 'people/new', element: <AddCandidatePage /> },
      { path: 'people/:id', element: <PersonPage /> },
      // Rows 1.3 and 1.4 (#261, #262). Person-scoped, one segment deeper than the record, and
      // spelled out so neither can be reached by a one-segment sibling such as `people/new`.
      { path: 'people/:id/offer/:recordId/review', element: <OfferReviewPage /> },
      { path: 'people/:id/offer/send', element: <OfferSendPage /> },
      { path: 'offers', element: <OffersPage /> },
      { path: 'roster', element: <RosterPage /> },
      { path: 'review', element: <ReviewQueuePage /> },
      { path: 'design', element: <DesignPage /> },
      /* plop:routes */
      { path: 'portal-links', element: <PortalLinksPage /> },
      { path: 'interviews', element: <InterviewsPage /> },
      // One employee's profile (#585). Under the Roster, so the Roster stays the highlighted page; the id is the opaque person id.
      { path: 'roster/:id', element: <EmployeeProfilePage /> },
      { path: 'forms', element: <FormsPage /> },
    ],
  },
  // Where sign-out lands (#718): public, and outside /app so arriving does not sign the person back in.
  { path: '/signed-out', element: <SignedOutPage /> },
  { path: '/portal', element: <PortalPage /> },
  { path: '*', element: <NotFoundPage /> },
];
